import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useConfirm } from '../components/ConfirmModal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

const KPI_LABELS = [
  { key: 'taskCompletion', label: 'Task Completion' },
  { key: 'quality',        label: 'Work Quality'    },
  { key: 'punctuality',    label: 'Punctuality'     },
  { key: 'teamwork',       label: 'Teamwork'        },
  { key: 'communication',  label: 'Communication'   },
  { key: 'initiative',     label: 'Initiative'      },
];

const RATING_COLOR = {
  excellent:     '#22c55e',
  good:          'var(--pm-primary)',
  average:       '#f59e0b',
  below_average: '#f97316',
  poor:          '#ef4444',
};

const RATING_LABEL = {
  excellent:     'Excellent',
  good:          'Good',
  average:       'Average',
  below_average: 'Below Avg',
  poor:          'Poor',
};

const S = {
  page:     { fontFamily:"'Sora',sans-serif" },
  title:    { fontSize: 24, fontWeight: 800, color: '#0f172a', marginBottom: 4 },
  sub:      { fontSize: 13, color: '#64748b', marginBottom: 24 },
  card:     { background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e2e8f0', boxShadow: '0 2px 12px #0000000a' },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 16, marginBottom: 28 },
  stat:     { background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #e2e8f0', textAlign: 'center', boxShadow: '0 2px 8px #0000000a' },
  btn:      { padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  table:    { width: '100%', borderCollapse: 'collapse' },
  th:       { padding: '10px 14px', background: '#f8fafc', color: '#475569', fontSize: 12, fontWeight: 600, textAlign: 'left', textTransform: 'uppercase', letterSpacing: 0.5 },
  td:       { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#475569' },
  badge:    (c) => ({ display:'inline-block', padding:'2px 10px', borderRadius:20, background: c+'22', color: c, fontSize:11, fontWeight:600 }),
  input:    { width: '100%', padding: '9px 12px', background: '#fff', border: '2px solid #e2e8f0', borderRadius: 8, color: '#0f172a', fontSize: 13, outline: 'none', boxSizing:'border-box' },
  label:    { fontSize: 12, color: '#475569', marginBottom: 4, display: 'block', fontWeight: 600 },
  modal:    { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 },
  mbox:     { background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:620, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 80px rgba(0,0,0,0.15)' },
};

const ScoreBar = ({ value, max = 10 }) => (
  <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
    <div style={{ flex:1, height:6, background:'#e2e8f0', borderRadius:3, overflow:'hidden' }}>
      <div style={{ width: `${(value/max)*100}%`, height:'100%', background:'var(--pm-primary)', borderRadius:3 }} />
    </div>
    <span style={{ fontSize:12, color:'#64748b', minWidth:28 }}>{parseFloat(value||0).toFixed(1)}</span>
  </div>
);

const EMPTY_FORM = {
  employeeId: '', period: '', reviewType: 'quarterly',
  taskCompletion: 8, quality: 8, punctuality: 8, teamwork: 8, communication: 8, initiative: 8,
  goals: '', strengths: '', improvements: '', reviewerComments: '',
};

export default function Performance() {
  const { user } = useAuth();
  const isAdmin  = user?.role === 'admin';
  const confirm = useConfirm();

  const [reviews,      setReviews]      = useState([]);
  const [employees,    setEmployees]    = useState([]);
  const [stats,        setStats]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [leaderboard,  setLeaderboard]  = useState([]);
  const [topMonth,     setTopMonth]     = useState(new Date().toISOString().slice(0,7));
  const [showModal,  setShowModal]  = useState(false);
  const [selected,   setSelected]   = useState(null);
  const [ackModal,   setAckModal]   = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [ackComment, setAckComment] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterPeriod) params.period = filterPeriod;
      const [rRes, sRes] = await Promise.all([
        axios.get(`${API}/performance`, { headers: headers(), params }),
        isAdmin ? axios.get(`${API}/performance/stats`, { headers: headers() }) : Promise.resolve(null),
      ]);
      setReviews(rRes.data.data || []);
      if (sRes) setStats(sRes.data.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [filterPeriod, isAdmin]);

  const loadLeaderboard = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const r = await axios.get(`${API}/performance/top-performer`, { headers: headers(), params: { month: topMonth } });
      setLeaderboard(r.data.leaderboard || []);
    } catch {}
  }, [topMonth, isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLeaderboard(); }, [loadLeaderboard]);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/users?role=team`, { headers: headers() })
      .then(r => setEmployees(r.data.data || r.data.users || []))
      .catch(() => {});
  }, [isAdmin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/performance`, form, { headers: headers() });
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { alert(e.response?.data?.message || 'Error'); }
  };

  const handleAck = async () => {
    try {
      await axios.put(`${API}/performance/${ackModal.id}/acknowledge`, { employeeComments: ackComment }, { headers: headers() });
      setAckModal(null);
      setAckComment('');
      setSelected(null);
      load();
    } catch (e) { alert(e.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (id) => {
    const ok = await confirm('Delete this review?', { title: 'Delete Review?', variant: 'danger' });
    if (!ok) return;
    await axios.delete(`${API}/performance/${id}`, { headers: headers() });
    load();
  };

  const previewScore = () => {
    const kpis = KPI_LABELS.map(k => parseFloat(form[k.key]) || 0);
    return (kpis.reduce((s, v) => s + v, 0) / kpis.length).toFixed(2);
  };

  const periods = [...new Set(reviews.map(r => r.period))].sort().reverse();

  return (
    <div style={S.page}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={S.title}>Performance Management</h1>
          <div style={S.sub}>KPI-based employee performance reviews & tracking</div>
        </div>
        {isAdmin && (
          <button style={{ ...S.btn, background:'var(--pm-gradient)', color:'#fff' }} onClick={() => setShowModal(true)}>
            + New Review
          </button>
        )}
      </div>

      {/* Stats cards — admin only */}
      {isAdmin && stats && (
        <div style={S.statGrid}>
          {[
            { l:'Total Reviews',    v: stats.total,                   c:'var(--pm-primary)' },
            { l:'Avg Score',        v: `${stats.avgScore}/10`,        c:'#22c55e' },
            { l:'Excellent',        v: stats.ratingCounts?.excellent, c:'#22c55e' },
            { l:'Good',             v: stats.ratingCounts?.good,      c:'var(--pm-primary)' },
            { l:'Average',          v: stats.ratingCounts?.average,   c:'#f59e0b' },
            { l:'Needs Attention',  v: (stats.ratingCounts?.below_average||0)+(stats.ratingCounts?.poor||0), c:'#ef4444' },
          ].map(s => (
            <div key={s.l} style={S.stat}>
              <div style={{ fontSize:26, fontWeight:800, color:s.c }}>{s.v}</div>
              <div style={{ fontSize:12, color:'#64748b', marginTop:4 }}>{s.l}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Top Performer Leaderboard ──────────────────────── */}
      {isAdmin && leaderboard.length > 0 && (
        <div style={{ ...S.card, marginBottom:28, padding:24 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
            <h3 style={{ margin:0, fontSize:17, fontWeight:800 }}>🏆 Top Performers</h3>
            <input type="month" value={topMonth} onChange={e=>setTopMonth(e.target.value)}
              style={{ padding:'7px 12px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:13, outline:'none' }}/>
          </div>

          {/* Podium top 3 */}
          {leaderboard.slice(0,3).length > 0 && (
            <div style={{ display:'flex', justifyContent:'center', gap:16, marginBottom:24, flexWrap:'wrap' }}>
              {leaderboard.slice(0,3).map((entry, i) => {
                const medals = ['🥇','🥈','🥉'];
                const sizes  = [80, 68, 60];
                const bgGrad = i===0 ? 'linear-gradient(135deg,#fbbf24,#f59e0b)' : i===1 ? 'linear-gradient(135deg,#94a3b8,#64748b)' : 'linear-gradient(135deg,#cd7f32,#b45309)';
                return (
                  <div key={entry.user.id} style={{ textAlign:'center', width:160 }}>
                    <div style={{ fontSize:28, marginBottom:6 }}>{medals[i]}</div>
                    <div style={{ width:sizes[i], height:sizes[i], borderRadius:'50%', background:bgGrad, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:sizes[i]*0.35, margin:'0 auto 10px', boxShadow:`0 4px 14px ${i===0?'#f59e0b44':'#0002'}` }}>
                      {entry.user.name.charAt(0)}
                    </div>
                    <div style={{ fontWeight:700, fontSize:14, color:'#0f172a' }}>{entry.user.name}</div>
                    <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{entry.user.position || entry.user.role}</div>
                    <div style={{ marginTop:8, padding:'4px 14px', borderRadius:20, background: i===0?'#fef9c3':i===1?'#f1f5f9':'#fef3e2', color: i===0?'#92400e':i===1?'#475569':'#92400e', fontWeight:700, fontSize:13, display:'inline-block' }}>
                      {entry.composite} pts
                    </div>
                    <div style={{ fontSize:11, color:'#64748b', marginTop:4 }}>
                      {entry.stats.completedTasks} tasks · {entry.stats.hoursLogged}h logged
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Full leaderboard table */}
          <table style={{ ...S.table, fontSize:13 }}>
            <thead>
              <tr>
                {['#','Employee','Tasks Done','Completion %','Hours Logged','Reports','Score','Points'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((entry, i) => (
                <tr key={entry.user.id} style={{ background: i===0?'#fefce8':i===1?'#f8fafc':i===2?'#fff8f0':'transparent' }}>
                  <td style={S.td}>{i===0?'🥇':i===1?'🥈':i===2?'🥉':i+1}</td>
                  <td style={{ ...S.td, fontWeight:700, color:'#0f172a' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ width:28, height:28, borderRadius:'50%', background:'var(--pm-gradient)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, flexShrink:0 }}>{entry.user.name.charAt(0)}</div>
                      <div>
                        <div>{entry.user.name}</div>
                        <div style={{ fontSize:11, color:'#94a3b8', fontWeight:400 }}>{entry.user.department || entry.user.role}</div>
                      </div>
                    </div>
                  </td>
                  <td style={S.td}>{entry.stats.completedTasks}</td>
                  <td style={S.td}>{entry.stats.completionRate}%</td>
                  <td style={S.td}>{entry.stats.hoursLogged}h</td>
                  <td style={S.td}>{entry.stats.reportsCount}</td>
                  <td style={S.td}>{entry.stats.reviewScore > 0 ? `${entry.stats.reviewScore}/10` : '—'}</td>
                  <td style={{ ...S.td, fontWeight:800, color: i===0?'#d97706':i===1?'#64748b':'#b45309' }}>{entry.composite}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ margin:'12px 0 0', fontSize:11, color:'#94a3b8' }}>Points = tasks completed × 3 + completion rate × 0.5 + hours × 0.1 + reports × 2 + review score × 5</p>
        </div>
      )}

      {/* Filter */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <select style={{ ...S.input, width:180 }} value={filterPeriod} onChange={e => setFilterPeriod(e.target.value)}>
          <option value="">All Periods</option>
          {periods.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? <div style={{ color:'#94a3b8', textAlign:'center', padding:40 }}>Loading...</div> : (
        <div style={S.card}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Employee</th>
                <th style={S.th}>Period</th>
                <th style={S.th}>Type</th>
                {KPI_LABELS.map(k => <th key={k.key} style={S.th}>{k.label}</th>)}
                <th style={S.th}>Overall</th>
                <th style={S.th}>Rating</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reviews.length === 0 && (
                <tr><td colSpan={9+KPI_LABELS.length} style={{ ...S.td, textAlign:'center', color:'#94a3b8', padding:32 }}>
                  No reviews yet{isAdmin ? '. Click "+ New Review" to create one.' : ''}
                </td></tr>
              )}
              {reviews.map(r => (
                <tr key={r.id} style={{ cursor:'pointer' }} onClick={() => setSelected(r)}>
                  <td style={S.td}>
                    <div style={{ fontWeight:600, color:'#0f172a' }}>{r.employee?.name}</div>
                    <div style={{ fontSize:11, color:'#94a3b8' }}>{r.employee?.position || r.employee?.email}</div>
                  </td>
                  <td style={S.td}>{r.period}</td>
                  <td style={S.td}><span style={{ textTransform:'capitalize' }}>{r.reviewType}</span></td>
                  {KPI_LABELS.map(k => (
                    <td key={k.key} style={{ ...S.td, minWidth:100 }}>
                      <ScoreBar value={r[k.key]} />
                    </td>
                  ))}
                  <td style={{ ...S.td, fontWeight:700, color:'var(--pm-primary)', fontSize:15 }}>
                    {parseFloat(r.overallScore||0).toFixed(1)}
                  </td>
                  <td style={S.td}>
                    <span style={S.badge(RATING_COLOR[r.rating] || '#94a3b8')}>
                      {RATING_LABEL[r.rating] || r.rating}
                    </span>
                  </td>
                  <td style={S.td}>
                    <span style={S.badge(r.status==='acknowledged'?'#22c55e':r.status==='submitted'?'#f59e0b':'#64748b')}>
                      {r.status}
                    </span>
                  </td>
                  <td style={S.td} onClick={e => e.stopPropagation()}>
                    <div style={{ display:'flex', gap:6 }}>
                      {!isAdmin && r.status === 'submitted' && r.employeeId === user.id && (
                        <button style={{ ...S.btn, background:'#22c55e22', color:'#22c55e', padding:'4px 10px' }}
                          onClick={() => { setAckModal(r); setAckComment(''); }}>
                          Acknowledge
                        </button>
                      )}
                      {isAdmin && (
                        <button style={{ ...S.btn, background:'#fee2e2', color:'#ef4444', padding:'4px 10px' }}
                          onClick={() => handleDelete(r.id)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div style={S.modal} onClick={() => setSelected(null)}>
          <div style={{ ...S.mbox, maxWidth:700 }} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
              <div>
                <div style={{ fontSize:18, fontWeight:700, color:'#0f172a' }}>
                  Performance Review — {selected.employee?.name}
                </div>
                <div style={{ fontSize:12, color:'#64748b' }}>{selected.period} · {selected.reviewType}</div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background:'none', border:'none', color:'#94a3b8', fontSize:20, cursor:'pointer' }}>✕</button>
            </div>

            {/* KPI Scores */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
              {KPI_LABELS.map(k => (
                <div key={k.key} style={{ background:'#f8fafc', borderRadius:8, padding:'12px 14px', border:'1px solid #e2e8f0' }}>
                  <div style={{ fontSize:12, color:'#64748b', marginBottom:6 }}>{k.label}</div>
                  <ScoreBar value={selected[k.key]} />
                </div>
              ))}
            </div>

            {/* Overall */}
            <div style={{ display:'flex', gap:16, marginBottom:20 }}>
              <div style={{ flex:1, background:'#f8fafc', borderRadius:8, padding:'12px 14px', textAlign:'center', border:'1px solid #e2e8f0' }}>
                <div style={{ fontSize:28, fontWeight:800, color:'var(--pm-primary)' }}>
                  {parseFloat(selected.overallScore||0).toFixed(1)}<span style={{ fontSize:14, color:'#94a3b8' }}>/10</span>
                </div>
                <div style={{ fontSize:12, color:'#64748b' }}>Overall Score</div>
              </div>
              <div style={{ flex:1, background:'#f8fafc', borderRadius:8, padding:'12px 14px', textAlign:'center', border:'1px solid #e2e8f0' }}>
                <div style={{ fontSize:22, fontWeight:700, color: RATING_COLOR[selected.rating] || '#94a3b8' }}>
                  {RATING_LABEL[selected.rating]}
                </div>
                <div style={{ fontSize:12, color:'#64748b' }}>Rating</div>
              </div>
            </div>

            {/* Text fields */}
            {[
              { f:'strengths',        l:'Strengths'             },
              { f:'improvements',     l:'Areas for Improvement' },
              { f:'goals',            l:'Goals for Next Period'  },
              { f:'reviewerComments', l:'Reviewer Comments'      },
              { f:'employeeComments', l:'Employee Comments'      },
            ].filter(({ f }) => selected[f]).map(({ f, l }) => (
              <div key={f} style={{ marginBottom:14 }}>
                <div style={{ fontSize:12, color:'var(--pm-primary)', fontWeight:600, marginBottom:4 }}>{l}</div>
                <div style={{ fontSize:13, color:'#475569', background:'#f8fafc', borderRadius:8, padding:'10px 12px', lineHeight:1.6, border:'1px solid #e2e8f0' }}>
                  {selected[f]}
                </div>
              </div>
            ))}

            {/* Acknowledge button */}
            {!isAdmin && selected.status === 'submitted' && selected.employeeId === user.id && (
              <button style={{ ...S.btn, background:'#22c55e', color:'#fff', marginTop:12, width:'100%' }}
                onClick={() => { setAckModal(selected); setSelected(null); }}>
                Acknowledge Review
              </button>
            )}
          </div>
        </div>
      )}

      {/* Acknowledge Modal */}
      {ackModal && (
        <div style={S.modal}>
          <div style={{ ...S.mbox, maxWidth:440 }}>
            <div style={{ fontSize:17, fontWeight:700, color:'#0f172a', marginBottom:16 }}>Acknowledge Review</div>
            <p style={{ color:'#64748b', fontSize:13, marginBottom:16 }}>
              By acknowledging, you confirm you've read and discussed this review with your manager.
            </p>
            <label style={S.label}>Your Comments (optional)</label>
            <textarea style={{ ...S.input, height:100, resize:'vertical' }}
              value={ackComment} onChange={e => setAckComment(e.target.value)}
              placeholder="Add any comments or feedback..." />
            <div style={{ display:'flex', gap:10, marginTop:16 }}>
              <button style={{ ...S.btn, background:'#22c55e', color:'#fff', flex:1 }} onClick={handleAck}>
                Confirm Acknowledge
              </button>
              <button style={{ ...S.btn, background:'#f1f5f9', color:'#64748b', flex:1 }} onClick={() => setAckModal(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Review Modal */}
      {showModal && (
        <div style={S.modal}>
          <div style={S.mbox}>
            <div style={{ fontSize:17, fontWeight:700, color:'#0f172a', marginBottom:20 }}>New Performance Review</div>
            <form onSubmit={handleSubmit}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={S.label}>Employee *</label>
                  <select style={S.input} value={form.employeeId} onChange={e => setForm(f => ({...f, employeeId: e.target.value}))} required>
                    <option value="">Select Employee</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Period (e.g. Q1-2026) *</label>
                  <input style={S.input} value={form.period} onChange={e => setForm(f => ({...f, period: e.target.value}))}
                    placeholder="Q1-2026" required />
                </div>
                <div>
                  <label style={S.label}>Review Type</label>
                  <select style={S.input} value={form.reviewType} onChange={e => setForm(f => ({...f, reviewType: e.target.value}))}>
                    <option value="quarterly">Quarterly</option>
                    <option value="annual">Annual</option>
                    <option value="probation">Probation</option>
                    <option value="adhoc">Ad-hoc</option>
                  </select>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, paddingTop:20 }}>
                  <span style={{ fontSize:13, color:'#64748b' }}>Preview Score:</span>
                  <span style={{ fontWeight:700, color:'var(--pm-primary)', fontSize:18 }}>{previewScore()}/10</span>
                </div>
              </div>

              {/* KPI Sliders */}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#475569', marginBottom:10 }}>KPI Scores (1–10)</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  {KPI_LABELS.map(k => (
                    <div key={k.key}>
                      <label style={{ ...S.label, display:'flex', justifyContent:'space-between' }}>
                        <span>{k.label}</span>
                        <span style={{ color:'var(--pm-primary)', fontWeight:700 }}>{form[k.key]}</span>
                      </label>
                      <input type="range" min="1" max="10" step="0.5"
                        style={{ width:'100%', accentColor:'var(--pm-primary)' }}
                        value={form[k.key]}
                        onChange={e => setForm(f => ({...f, [k.key]: parseFloat(e.target.value)}))} />
                    </div>
                  ))}
                </div>
              </div>

              {[
                { k:'strengths',        l:'Strengths',             ph:'Key strengths observed...' },
                { k:'improvements',     l:'Areas for Improvement', ph:'Areas to improve...' },
                { k:'goals',            l:'Goals for Next Period',  ph:'Set clear goals for next review...' },
                { k:'reviewerComments', l:'Reviewer Comments',      ph:'Additional comments...' },
              ].map(({ k, l, ph }) => (
                <div key={k} style={{ marginBottom:12 }}>
                  <label style={S.label}>{l}</label>
                  <textarea style={{ ...S.input, height:70, resize:'vertical' }}
                    placeholder={ph}
                    value={form[k]}
                    onChange={e => setForm(f => ({...f, [k]: e.target.value}))} />
                </div>
              ))}

              <div style={{ display:'flex', gap:10, marginTop:8 }}>
                <button type="submit" style={{ ...S.btn, background:'var(--pm-gradient)', color:'#fff', flex:1 }}>
                  Submit Review
                </button>
                <button type="button" style={{ ...S.btn, background:'#f1f5f9', color:'#64748b', flex:1 }}
                  onClick={() => { setShowModal(false); setForm(EMPTY_FORM); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
